import { type FC, type ReactNode, type Ref, useEffect, useState } from "react";
import { cn } from "../../lib/utils";
import { ClickableIcon, Icon, type IconColor, type IconType } from "../../primitives";
import { Card } from "../../primitives/Card";
import { TimeProgressBar } from "../../primitives/TimeProgressBar";

export const FLAG_AUTO_DISMISS_TIMEOUT = 8000;

export const FLAG_TONES = ["info", "warning", "error", "success"] as const;
export type FlagTone = (typeof FLAG_TONES)[number];

// Tone-keyed defaults for the leading icon.
const TONE_ICON: Record<FlagTone, { type: IconType; color: IconColor }> = {
	info: { type: "information-circle", color: "accent" },
	warning: { type: "exclamation-triangle", color: "warning" },
	error: { type: "exclamation-circle", color: "danger" },
	success: { type: "check-circle", color: "confirm" },
};

// Tone-colored left accent stripe — the severity cue. Scoped to the content row.
const TONE_STRIPE: Record<FlagTone, string> = {
	info: "border-l-brand-accent",
	warning: "border-l-warning",
	error: "border-l-danger",
	success: "border-l-confirm",
};

export const getTestIdForFlagTone = (tone: FlagTone): string => `${tone}-flag-container`;

export type FlagProps = {
	/** Severity of the flag. Drives the accent stripe and the default icon. */
	tone: FlagTone;
	heading?: string;
	message: string;
	/**
	 * Controls auto-dismiss. `true` dismisses after the default timeout, a number
	 * sets a custom timeout in ms, `false`/`undefined` disables auto-dismiss.
	 */
	autoDismiss?: boolean | number;
	/** Invoked on dismissal — close button or auto-dismiss timeout. */
	onClose: () => void;
	/**
	 * ARIA live-region role. Defaults to `"alert"` (assertive) for the `error`
	 * and `warning` tones and `"status"` (polite) otherwise, so screen readers
	 * announce newly queued flags.
	 */
	role?: "alert" | "status";
	trackingId?: string;
	/** Overrides the root `data-testid`. Defaults to the tone-derived id. */
	testId?: string;
	/** Extra content rendered in the flag body, below the message. */
	children?: ReactNode;
	ref?: Ref<HTMLDivElement>;
};

export const Flag: FC<FlagProps> = ({
	tone,
	heading,
	message,
	autoDismiss = false,
	onClose,
	role,
	trackingId,
	testId,
	children,
	ref,
}) => {
	const liveRegionRole = role ?? (tone === "error" || tone === "warning" ? "alert" : "status");
	const autoDismissMs =
		autoDismiss === true
			? FLAG_AUTO_DISMISS_TIMEOUT
			: typeof autoDismiss === "number" && autoDismiss > 0
				? autoDismiss
				: null;
	const [autoDismissing, setAutoDismissing] = useState(autoDismissMs !== null);

	useEffect(() => {
		if (!autoDismissing || autoDismissMs === null) return;
		const timer = setTimeout(onClose, autoDismissMs);
		return () => clearTimeout(timer);
	}, [autoDismissing, autoDismissMs, onClose]);

	return (
		<Card
			ref={ref}
			role={liveRegionRole}
			tabIndex={0}
			onFocus={() => setAutoDismissing(false)}
			onBlur={() => setAutoDismissing(autoDismissMs !== null)}
			data-testid={testId ?? getTestIdForFlagTone(tone)}
			trackingId={trackingId}
			className="flex flex-col overflow-hidden rounded border-l-0 w-auto max-w-72 text-primary shadow-lg"
		>
			<div className={cn("flex flex-row gap-2 border-l-2 p-3 pr-8", TONE_STRIPE[tone])}>
				<div className="shrink-0">
					<Icon type={TONE_ICON[tone].type} color={TONE_ICON[tone].color} size="medium" />
				</div>
				<div className="min-w-0 flex-1">
					{heading && <strong className="font-semibold text-primary">{heading}</strong>}
					<p className="mt-1 text-sm text-secondary">{message}</p>
					{children}
				</div>
			</div>
			<ClickableIcon
				aria-label="Dismiss"
				data-testid="flag-close-button"
				type="x-mark"
				size="small"
				color="tertiary"
				onClick={onClose}
				className="absolute right-2 top-2"
			/>
			{autoDismissing && (
				<div className="flex w-full">
					<TimeProgressBar totalDurationMs={autoDismissMs!} startFull />
				</div>
			)}
		</Card>
	);
};
Flag.displayName = "Flag";
